import {Component, EventEmitter, Inject} from '@angular/core';
import {ProjectId} from '../../types/ids';
import {Browser, browserFromBackendObject} from '../../types/browser';
import {map} from 'rxjs/operators';

import * as _ from 'lodash';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';
import {
    BasicAuthInfo,
    BrowserSpecification,
    ClickDriverSpecification,
    DriverSpecification,
    DriverSpecificationType,
    ExpeditionComparisonTestConfig,
    LiveExpeditionSpec,
    SequentialDriverSpecification,
    URLListDriverSpecification,
} from '@webmate-web-core/shared/types/testmgmt';
import {VehicleService} from '@webmate-web-core/shared/services/vehicle.service';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {BAConfig, ClickDriverConfigElement,} from '@webmate-web-core/shared/types/test-config-helpers';
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {SelectChangeEvent} from 'primeng/select';

const BASICAUTH_ISSUE_KEY = 'basicauth';

export class CrossbrowserTestDialogToolConfig implements WebmateToolConfig {
    toolName = 'Configure Cross-Browser Test';
    scrollable = true;

    constructor(public projectId: ProjectId, public defaults: ExpeditionComparisonTestConfig, public createCallback: (conf: ExpeditionComparisonTestConfig) => void, public closeAfterCallback: boolean, public editMode: boolean = false, public existingNames: Array<string> = [], public btnLabel = 'EXECUTE TEST') {
        if (defaults.name) {
            this.toolName =  `Configure ${defaults.name}`;
        }
    }
}

@Component({
    templateUrl: './crossbrowser-test-dialog.component.html',
    styleUrls: ['./crossbrowser-test-dialog.component.less'],
})
export class CrossbrowserTestDialogComponent {

    projectId: ProjectId;

    constructor(@Inject(TOOL_CONTAINER_DATA) public data: CrossbrowserTestDialogToolConfig, private deviceService: DeviceService,
                private toolService: ModalToolContainerService, private vehicleService: VehicleService) {
    }

    name: string = '';
    description: string = '';

    urls: string[] = [''];

    availableBrowsers: Browser[] = [];
    availableReferenceBrowsers: Browser[] = [];
    deployedBrowsers: Browser[] = [];

    simplifiedRef = true;
    chosenReferenceBrowser?: Browser = undefined;

    chosenCrossBrowsers: Array<Browser | undefined> = [];

    clearBrowser = new EventEmitter<void>();
    clearCrossBrowsers: EventEmitter<void>[] = [];

    busy: boolean = true;

    basicAuthData: BAConfig[] = [];
    baUrlOptions: Array<{value: string; label: string}> = [];

    clickDriverConfig: ClickDriverConfigElement[] = [];
    referenceAutomationPolicy?: AutomationPolicyConfig;

    defaultViewports: Array<any> = [
        {'width': 600, 'height': 800 /* we ignore height for now */},
        {'width': 800, 'height': 800 /* we ignore height for now */},
        {'width': 1024, 'height': 800 /* we ignore height for now */},
        {'width': 1280, 'height': 800 /* we ignore height for now */},
        {'width': 1366, 'height': 800 /* we ignore height for now */},
        {'width': 1440, 'height': 800 /* we ignore height for now */},
        {'width': 1680, 'height': 800 /* we ignore height for now */},
        {'width': 1920, 'height': 800 /* we ignore height for now */},
    ].map(x => {
        return {value: x, label: `${x.width} px`};
    });
    customViewportSelected: boolean = false;
    customViewportWidth: number = this.defaultViewports[3].value.width;
    chosenViewport: any = this.defaultViewports[3].value;

    browserWithoutBasicAuthSelected: boolean = false;
    basicAuthErrorMessage?: string = undefined;

    advancedSettingsShown = false;

    isCreatingTest = false;

    ngOnInit() {
        this.name = this.data.defaults.name ? this.data.defaults.name : '';
        this.description = this.data.defaults.description ? this.data.defaults.description : '';

        if (this.data.defaults.referenceExpeditionSpec && this.data.defaults.referenceExpeditionSpec.data.type === 'LiveExpeditionSpec') {
            const referenceSpec = this.data.defaults.referenceExpeditionSpec.data as LiveExpeditionSpec;
            this.referenceAutomationPolicy = referenceSpec.automationPolicy;
            if (referenceSpec.vehicleSpec) {
                this.chosenReferenceBrowser = browserFromBackendObject(referenceSpec.vehicleSpec.browser);
            }
            if (referenceSpec.driverSpec) {
                switch (referenceSpec.driverSpec.type) {
                    case 'URLListDriverSpecification':
                        this.urls = (referenceSpec.driverSpec as URLListDriverSpecification).urls;
                        if (this.urls.length === 0) {
                            this.urls = [''];
                        }
                        break;

                    case 'SequentialDriverSpecification': {
                        let rootDriverSpecification = referenceSpec.driverSpec as SequentialDriverSpecification;

                        let urlListDrivers = rootDriverSpecification.specs.filter(spec => spec.type == 'URLListDriverSpecification') as Array<URLListDriverSpecification>;
                        let clickDrivers = rootDriverSpecification.specs.filter(spec => spec.type == 'ClickDriverSpecification') as Array<ClickDriverSpecification>;
                        if (urlListDrivers.length != 1 || clickDrivers.length != 1) {
                            break;
                        }

                        this.urls = urlListDrivers[0].urls;
                        if (this.urls.length === 0) {
                            this.urls = [''];
                        }
                        this.clickDriverConfig = clickDrivers[0].specs.map(spec => {
                            return {url: spec.url, xpaths: spec.xpaths} as ClickDriverConfigElement;
                        });
                        break;
                    }
                    default:
                        break;
                }
            }
            let ba = referenceSpec.basicAuth;
            if (ba && ba.length > 0) {
                ba.forEach(e => {
                    let existing = this.basicAuthData.find(value => value.username === e.basic_auth_username && value.password === e.basic_auth_password);
                    if (!!existing) {
                        existing.urls.push(e.basic_auth_domain);
                    } else {
                        this.basicAuthData.push({
                            urls: [e.basic_auth_domain],
                            username: e.basic_auth_username,
                            password: e.basic_auth_password
                        });
                    }
                });
            }
            this.updateBaUrlOptions();
            if (this.basicAuthData.length === 0) {
                this.addBAConfig();
            }
        }

        if (this.data.defaults.comparisonExpeditionSpecs && this.data.defaults.comparisonExpeditionSpecs.type === 'List[ExpeditionSpec]') {
            this.data.defaults.comparisonExpeditionSpecs.data.forEach(s => {
                if (s.type === 'LiveExpeditionSpec') {
                    this.addNewBrowser();
                    this.chosenCrossBrowsers[this.chosenCrossBrowsers.length - 1] = browserFromBackendObject((s as LiveExpeditionSpec).vehicleSpec.browser);
                }
            });
        } else {
            this.addNewBrowser();
        }

        this.projectId = this.data.projectId;

        this.vehicleService.getVehicleOffersForProject$(this.projectId).subscribe(offers => {
            const browsers = offers.map(o => browserFromBackendObject(o!.properties['vehicle.browser']));
            this.availableBrowsers = browsers
                .filter(b => !!b && b!.isActualBrowser())
                .filter(b => !b!.isMobile())
                .sort((a, b) => a!.browserType.localeCompare(b!.browserType));
            this.availableReferenceBrowsers = this.availableBrowsers.filter(b => b!.browserType.toLowerCase() === 'chrome' || b!.browserType.toLowerCase() === 'firefox');
            this.busy = false;
        });

        this.deviceService.getDevicesForProject$(this.projectId).pipe(
            map(devices => {
                return devices.filter(d => d!.isAvailableToJobs() && d!.state === 'running' && !d!.isHeadlessDevice());
            })
        ).subscribe(devices => {
            devices.forEach(d => {
                const machineBrowsers = (d!.properties[DevicePropertyNames.MachineBrowsers] as Array<any>) || [];
                const deployed = machineBrowsers.map(a => browserFromBackendObject(a))
                    .filter(b => b!.isActualBrowser())
                    .filter(b => !b!.isMobile());
                this.deployedBrowsers = [...this.deployedBrowsers, ...deployed];
            });
        });
    }

    addUrlListItem() {
        this.urls.push('');
    }

    removeUrlListItem(index: number) {
        this.urls.splice(index, 1);
    }

    clearUrl(index: number) {
        this.urls[index] = '';
    }

    trackByIndex(index: number, obj: any): any {
        return index;
    }

    updateBasicAuthIssues() {
        let tmpErrorMsg = 'Some Browser(s) you selected do not support Basic Authentication. The test execution will likely fail for: \n\n';
        if (this.chosenReferenceBrowser) {
            let refTmp = !(this.chosenReferenceBrowser?.capabilities?.['service.browserMobSupport'] ?? false);
            if (refTmp) {
                tmpErrorMsg += ' - ' + this.chosenReferenceBrowser.getFullName() + '\n\n';
            }
            this.browserWithoutBasicAuthSelected = refTmp;
        }
        for (let b of this.chosenCrossBrowsers) {
            if (!b) continue;
            let crossTmp = !(b.capabilities?.['service.browserMobSupport'] ?? false);
            if (crossTmp) {
                tmpErrorMsg += ' - ' + b.getFullName() + '\n\n';
            }
            this.browserWithoutBasicAuthSelected = this.browserWithoutBasicAuthSelected || crossTmp;
        }
        if (this.browserWithoutBasicAuthSelected) {
            this.basicAuthErrorMessage = tmpErrorMsg;
        } else {
            this.basicAuthErrorMessage = undefined;
        }
    }

    trimURLsHelper(s: string) {
        return s.trim().length;
    }

    setReferenceBrowser(browser: Browser) {
        if (this.data.editMode && this.chosenReferenceBrowser && browser.equalsOtherBrowser(this.chosenReferenceBrowser)) {
            this.chosenReferenceBrowser = undefined;
            this.clearBrowser.emit();
        } else {
            this.chosenReferenceBrowser = browser;
        }
        this.updateBasicAuthIssues();
    }

    addNewBrowser() {
        //this is the browser which is used to initialize each crossbrowser
        this.chosenCrossBrowsers.push(undefined);
        this.clearCrossBrowsers.push(new EventEmitter<void>());
    }

    updateCrossBrowser(browser: Browser, index: number) {
        if (this.data.editMode && this.chosenCrossBrowsers[index] && browser.equalsOtherBrowser(this.chosenCrossBrowsers[index]!)) {
            this.chosenCrossBrowsers[index] = undefined;
            this.clearCrossBrowsers[index].emit();
        } else {
            this.chosenCrossBrowsers[index] = browser;
        }
        this.updateBasicAuthIssues();
    }

    removeCrossBrowser(index: number) {
        this.chosenCrossBrowsers.splice(index, 1);
        this.clearCrossBrowsers.splice(index, 1);
        this.updateBasicAuthIssues();
    }

    addAndChooseCustomViewport() {
        this.customViewportSelected = true;
    }

    allCrossBrowsersDefined() {
        let tmp = this.chosenCrossBrowsers.filter(value => !!value);
        return tmp.length > 0 && tmp.length == this.chosenCrossBrowsers.length;
    }

    getSaneViewport(): any {
        let vpInput = this.customViewportWidth ? this.customViewportWidth : 300;
        return this.customViewportSelected ? {
            'width': Math.max(300, Math.min(vpInput, 1920)),
            'height': 800 /* we ignore height for now */
        } : this.chosenViewport;
    }

    createTest() {
        this.isCreatingTest = true;
        let conf = {} as ExpeditionComparisonTestConfig;
        let driverSpec: DriverSpecification;
        let baData: BasicAuthInfo[] = [];
        if (this.basicAuthData.length > 0) {
            baData = _.flatten(this.basicAuthData.filter(entry => entry.username.trim().length > 0 && entry.password.trim().length > 0).map(entry => {
                return entry.urls.map(u => {
                    return {
                        basic_auth_domain: u,
                        basic_auth_username: entry.username,
                        basic_auth_password: entry.password
                    };
                });
            }));
        }

        if (this.clickDriverConfig.length >= 1) {
            driverSpec = {
                type: 'SequentialDriverSpecification' as DriverSpecificationType,
                specs: [
                    {
                        type: 'ClickDriverSpecification' as DriverSpecificationType,
                        specs: this.clickDriverConfig
                    },
                    {
                        urls: this.urls.filter(u => u.length > 0),
                        type: 'URLListDriverSpecification'
                    } as DriverSpecification

                ]
            };
        } else {
            let urls = this.urls.filter(u => u.length > 0);
            if (urls.length > 0) {
                driverSpec = {
                    urls: urls,
                    type: 'URLListDriverSpecification' as DriverSpecificationType
                };
            }
        }

        let refVehicle: BrowserSpecification;
        if (this.chosenReferenceBrowser) {
            refVehicle = {
                // remove browser properties like selenium version to avoid a wrong version being used when running the test
                browser: this.chosenReferenceBrowser.withoutProperties(),
                useProxy: baData.length > 0,
                type: 'BrowserSpecification'
            } as BrowserSpecification;
        }

        if (driverSpec! || refVehicle!) {
            conf.referenceExpeditionSpec = {
                data: {
                    type: 'LiveExpeditionSpec',
                    driverSpec: driverSpec!,
                    vehicleSpec: refVehicle!,
                    basicAuth: baData,
                    automationPolicy: this.referenceAutomationPolicy,
                },
                type: 'ExpeditionSpec'
            };
        }

        let filteredCross = this.chosenCrossBrowsers.filter(x => !!x).map(b => b!.withoutProperties());
        if (filteredCross.length > 0) {
            conf.comparisonExpeditionSpecs = {
                data: filteredCross.map(b => {
                    return {
                        type: 'LiveExpeditionSpec',
                        driverSpec: driverSpec,
                        basicAuth: baData,
                        vehicleSpec: {
                            browser: b,
                            useProxy: baData.length > 0,
                            type: 'BrowserSpecification'
                        } as BrowserSpecification,

                    };
                }),
                type: 'List[ExpeditionSpec]'
            };
        }
        conf.testVisualisationType = {type: 'String', data: 'crossbrowser_test'};

        if (this.data.editMode) {
            conf.name = this.name;
            conf.description = this.description;
        }
        this.data.createCallback(conf);
        if (this.data.closeAfterCallback) {
            this.toolService.closeLastTool();
        }
    }

    checkURLValidity(): boolean {
        if (this.urls.map(this.trimURLsHelper).indexOf(0) === -1) {
            for (let url of this.urls) {
                if (url.length === 0 || !this._urlValid(url)) return false;
            }
            return true;
        } else {
            return false;
        }
    }

    checkURLValidityAllowEmpty() {
        // TODO combine all 3 versions
        for (let url of this.urls) {
            if (url.length === 0) continue;
            if (!this._urlValid(url)) return false;
        }
        return true;
    }

    private _urlValid(url: string) {
        if (url.indexOf('.') === -1) return false;
        if (url.indexOf('://') === -1) return false;
        if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
        if (url.indexOf('://') !== url.lastIndexOf('://')) return false;
        return true;
    }

    existingURLValid(idx: number): boolean {
        if (idx >= this.urls.length) return false;

        let url = this.urls[idx];
        if (!url || (url.length === 0 && !this.data.editMode)) return false;

        return this._urlValid(url);
    }

    toggleAdvancedSettings() {
        this.advancedSettingsShown = !this.advancedSettingsShown;
    }

    toggleRef() {
        this.simplifiedRef = !this.simplifiedRef;
    }

    settingsValid() {
        if (this.data.editMode) {
            return !this.nameInvalid() &&
                this.checkURLValidityAllowEmpty() &&
                this.clickdriversValidAllowEmpty();
            ;
        } else {
            return !!this.chosenReferenceBrowser &&
                _.every(this.chosenCrossBrowsers, x => !!x) &&
                this.urls.length > 0 &&
                this.checkURLValidity() &&
                this.clickdriversValid();
        }
    }

    clickdriversValid() {
        if (this.clickDriverConfig.length == 0) {
            return true;
        }
        return this.clickDriverConfig.every(clickDriver => this.clickdriverValid(clickDriver));
    }

    clickdriversValidAllowEmpty() {
        if (this.clickDriverConfig.length == 0) {
            return true;
        }
        for (let el of this.clickDriverConfig) {
            if (el.url.length > 0 && !this._urlValid(el.url)) return false;
        }
        return true;
    }

    private _xPathsValid(xpaths: string[]) {
        return (xpaths.length == 0) || (xpaths.every(xpath => xpath.length != 0));
    }

    clickdriverValid(clickDriver: ClickDriverConfigElement) {
        return clickDriver.url.length !== 0 && this._urlValid(clickDriver.url) && this._xPathsValid(clickDriver.xpaths);
    }

    addUrlToClickdriver() {
        this.clickDriverConfig.push({
            url: '',
            xpaths: ['']
        } as ClickDriverConfigElement);
    }

    addXpathToClickdriverURL(clickdriverId: number) {
        this.clickDriverConfig[clickdriverId].xpaths.push('');
    }

    removeClickDriverConfig(clickdriverId: number) {
        this.clickDriverConfig.splice(clickdriverId, 1);
    }

    removeXpathFromClickDriverConfig(clickdriverId: number, xpathid: number) {
        this.clickDriverConfig[clickdriverId].xpaths.splice(xpathid, 1);
    }

    nameInvalid() {
        return this.name.trim().length === 0 || this.data.existingNames.find(n => n === this.name.trim());
    }

    getAllUrlDomains() {
        return this.urls.filter(u => this._urlValid(u)).map(url => {
            let u = new URL(url);
            return u.host;
        });
    }

    getUnusedUrlDomains() {
        return this.getAllUrlDomains().filter(d => !this.basicAuthData.find(ba => ba.urls.find(u => u === d)));
    }

    updateBaUrlOptions() {
        this.baUrlOptions = this.getUnusedUrlDomains().map(d => {
            return {
                value: d,
                label: d
            };
        });
        let allUrlDomains = this.getAllUrlDomains();

        this.basicAuthData.forEach((ba, idx) => {
            ba.urls = ba.urls.filter(url => allUrlDomains.includes(url))
        });
    }

    selectBaUrl(event: SelectChangeEvent, idx: number) {
        this.basicAuthData[idx].urls.push(event.value);
        this.updateBaUrlOptions();
    }

    removeBaUrl(baIdx: number, urlIdx: number) {
        this.basicAuthData[baIdx].urls.splice(urlIdx, 1);
        this.updateBaUrlOptions();
    }

    addBAConfig() {
        this.basicAuthData.push({
            urls: [],
            username: '',
            password: '',
        });
    }

    removeBAConfig(idx: number) {
        this.basicAuthData.splice(idx, 1);
        this.updateBaUrlOptions();
    }

}
